// PaymentSuccess.js - Handle payment redirects from PayMongo
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import { api } from '../services/api';

const PAYMONGO_PUBLIC_KEY = process.env.REACT_APP_PAYMONGO_PUBLIC_KEY;
const PAYMONGO_API_URL = 'https://api.paymongo.com/v1';

const encodeKey = (key) => btoa(key + ':');

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('checking'); // checking, success, failed, error
  const [message, setMessage] = useState('Verifying your payment...');
  const [planName, setPlanName] = useState('');

  useEffect(() => {
    verifyPayment();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const verifyPayment = async () => {
    try {
      // Get parameters from URL
      const paymentIntentId = searchParams.get('payment_intent_id') || 
                             searchParams.get('paymentIntentId');
      const clientKey = searchParams.get('client_key') || 
                       searchParams.get('clientKey');
      const plan = searchParams.get('plan');
      const billingPeriod = searchParams.get('billing') || 'monthly';

      console.log('🔍 Verifying payment...', { paymentIntentId, plan, billingPeriod });

      if (!paymentIntentId) {
        setStatus('error');
        setMessage('Missing payment information. Please try again.');
        return;
      }

      // Check payment status with PayMongo
      const response = await fetch(
        `${PAYMONGO_API_URL}/payment_intents/${paymentIntentId}${clientKey ? `?client_key=${clientKey}` : ''}`,
        {
          headers: {
            'Authorization': `Basic ${encodeKey(PAYMONGO_PUBLIC_KEY)}`
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to verify payment status');
      }

      const data = await response.json();
      const paymentStatus = data.data.attributes.status;
      const paymentPlan = data.data.attributes.metadata?.plan || plan;

      console.log('Payment status:', paymentStatus);

      if (paymentStatus === 'succeeded') {
        // Payment succeeded! Confirm with backend
        setMessage('Payment successful! Updating your account...');
        setPlanName(paymentPlan);

        const confirmResponse = await api('/api/confirm-payment', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            payment_intent_id: paymentIntentId,
            billingPeriod: billingPeriod
          })
        });

        if (confirmResponse && confirmResponse.ok) {
          setStatus('success');
          setMessage(`Successfully upgraded to ${paymentPlan} plan!`);
          
          // Redirect to dashboard after 3 seconds
          setTimeout(() => {
            navigate('/dashboard');
          }, 3000);
        } else {
          throw new Error(confirmResponse?.error || 'Failed to confirm payment');
        }
      } else if (paymentStatus === 'processing') {
        // Still processing, wait and check again
        setMessage('Payment is being processed...');
        setTimeout(() => verifyPayment(), 2000);
      } else if (paymentStatus === 'awaiting_payment_method') {
        // Payment failed
        setStatus('failed');
        const errorMsg = data.data.attributes.last_payment_error || 'Payment was declined';
        setMessage(`Payment failed: ${errorMsg}`);
      } else {
        // Other status
        setStatus('failed');
        setMessage(`Payment status: ${paymentStatus}`);
      }

    } catch (error) {
      console.error('Payment verification error:', error);
      setStatus('error');
      setMessage(error.message || 'Failed to verify payment. Please contact support.');
    }
  };

  return (
    <div className="min-h-screen bg-[#070b14] flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <Card.Header>
          <div className="text-center">
            {status === 'checking' && (
              <div className="mx-auto w-16 h-16 mb-4">
                <div className="animate-spin rounded-full h-16 w-16 border-4 border-emerald-500/20 border-t-emerald-500"></div>
              </div>
            )}
            
            {status === 'success' && (
              <div className="mx-auto w-16 h-16 mb-4 bg-emerald-500/15 rounded-full flex items-center justify-center">
                <svg className="w-10 h-10 text-emerald-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              </div>
            )}
            
            {status === 'failed' && (
              <div className="mx-auto w-16 h-16 mb-4 bg-red-500/15 rounded-full flex items-center justify-center">
                <svg className="w-10 h-10 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </div>
            )}
            
            {status === 'error' && (
              <div className="mx-auto w-16 h-16 mb-4 bg-amber-500/15 rounded-full flex items-center justify-center">
                <svg className="w-10 h-10 text-amber-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
            )}

            <Card.Title className={`text-2xl font-bold ${
              status === 'success' ? 'text-emerald-400' : 
              status === 'failed' ? 'text-red-400' : 
              status === 'error' ? 'text-amber-400' :
              'text-white'
            }`}>
              {status === 'checking' && 'Verifying Payment'}
              {status === 'success' && 'Payment Successful!'}
              {status === 'failed' && 'Payment Failed'}
              {status === 'error' && 'Verification Error'}
            </Card.Title>
            
            <Card.Description className="mt-2">
              {message}
            </Card.Description>

            {status === 'success' && planName && (
              <div className="mt-4 p-4 bg-emerald-500/10 border border-emerald-500/20 rounded-lg">
                <p className="text-sm text-emerald-300">
                  🎉 Welcome to the <strong className="capitalize">{planName}</strong> plan!
                </p>
                <p className="text-xs text-emerald-400/70 mt-1">
                  Redirecting to dashboard...
                </p>
              </div>
            )}
          </div>
        </Card.Header>

        {(status === 'failed' || status === 'error') && (
          <Card.Footer>
            <div className="flex flex-col sm:flex-row gap-3 w-full">
              <Button
                variant="outline"
                onClick={() => navigate('/user-pricing')}
                className="flex-1"
              >
                Try Again
              </Button>
              <Button
                variant="primary"
                onClick={() => navigate('/dashboard')}
                className="flex-1"
              >
                Go to Dashboard
              </Button>
            </div>
          </Card.Footer>
        )}

        {status === 'success' && (
          <Card.Footer>
            <Button
              variant="primary"
              onClick={() => navigate('/dashboard')}
              className="w-full"
            >
              Go to Dashboard Now
            </Button>
          </Card.Footer>
        )}
      </Card>
    </div>
  );
}
